import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { LinkOutlined } from "@ant-design/icons";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Alert,
  Button,
  Drawer,
  Image,
  List,
  Pagination,
  Skeleton,
  Tag,
  Tooltip,
  message,
} from "antd";
import { isEmpty, isUndefined } from "lodash";
import { useSelector } from "react-redux";
import {
  GET_LOCATION_VEHICLE_INVENTORIES_V2,
  GET_LOCATION_VEHICLE_INVENTORY,
} from "../../../graphql/query";
import { CREATE_VDP_LINK_INVENTORY } from "../../../graphql/mutation";
import { currencies } from "../../../library/constants";
import { formatPrice, getHighlightedText } from "../../../library/utils";
import useLang from "../../../hooks/useLang";
import "./vdpSelector.css";
import DebounceInput from "../../commonComponents/DebounceInput";
import VehicleMoreDetails from "./VDP/VehicleMoreDetails";
import VehicleInventoryDetailsDrawer from "../../inventory/VehicleInventoryDetailsDrawer";
import VdpLocationSearch from "./VdpLocationSearch";

// This is locationId of murdock hyundai location
const MURDOCK_LOCATION_ID = 448;

// VIN / stock number are the only identifiers shared between the lead's
// vehicles of interest and the inventory rows listed here, so they are what
// we match on. `leadVehicleInventory[].id` is a LeadVehicleOfInterest id, not
// a vehicle-inventory id, and can't be compared against the list.
const normalizeVehicleKey = value =>
  value === null || value === undefined ? "" : String(value).trim().toUpperCase();

// Reusable DetailRow component
const DetailRow = ({ label, value }) => (
  <div className="flex gap-3 text-sm font-normal">
    <span className="text-black/[0.65] w-[4.5rem] flex-shrink-0 text-right">{label}</span>
    <span className="flex-1 text-black/[0.88] break-words"> : {value}</span>
  </div>
);

const VDPSelector = forwardRef((props, ref) => {
  const [lang] = useLang();
  const {
    text,
    setmessageText,
    handleInsertLink = () => {},
    onClose,
    onSelectedVehiclesChange,
    // Optional override for what the selected vehicles resolve to. Default is
    // the VDP-links mutation (returns a newline-joined string to cursor-insert).
    // The OttoVin panel passes its own resolver that mints lead-attributed
    // OttoVin short links; with ottoVinInsert set it returns a { VIN: shortUrl }
    // map instead, and each link is composed into a "Year Make Model Stock# link" line.
    resolveLinks,
    // OttoVin insertion mode: compose one "Year Make Model Stock_Number: OttoVin_Link" line
    // per selected vehicle and append the block at the bottom of the composer
    // (a blank line separates it from any existing message), instead of the
    // default cursor-splice of bare links.
    ottoVinInsert = false,
    // Optional (OttoVin panel): the lead's vehicle of interest. When set and
    // the user hasn't typed a search, the list is seeded with SIMILAR
    // inventory (query seeded by the VOI's model/make) and the exact VOI
    // (VIN match) is pinned to the top. Typing a search replaces all of this
    // with normal search results.
    vehicleOfInterest,
  } = props;
  const { textMode, selectedLead } = useSelector(state => state.lead);
  const [selectedVehicle, setselectedVehicle] = useState([]);
  // id -> { vin, year, make, model, stockNumber } for currently-selected vehicles, so the
  // OttoVin "Year Make Model Stock# link" lines can be built at insert time even after
  // the visible list changes (e.g. the user searched after selecting).
  const selectedInfoRef = useRef(new Map());
  // Vehicles can be selected on one page and inserted after paginating or
  // searching away, so remember each selected vehicle's name by id.
  const vehicleNamesRef = useRef(new Map());
  const [search, setsearch] = useState("");
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50 });
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [moreDetailsDrawerOpen, setMoreDetailsDrawerOpen] = useState(false);
  const [selectedVehicleForDetails, setSelectedVehicleForDetails] = useState(null);
  const [inventoryDetailsDrawerOpen, setInventoryDetailsDrawerOpen] = useState(false);
  const [selectedVehicleForInventoryDetails, setSelectedVehicleForInventoryDetails] =
    useState(null);

  const currentLocationId = +sessionStorage.getItem("currentLocationId");
  const isMurdockLocation = currentLocationId === MURDOCK_LOCATION_ID;

  const [getVehicleInventory, { data: vdpData, loading: vdpLoader }] = useLazyQuery(
    GET_LOCATION_VEHICLE_INVENTORY,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  const [getVehicleInventoriesV2, { data: vdpDataV2, loading: vdpLoaderV2 }] = useLazyQuery(
    GET_LOCATION_VEHICLE_INVENTORIES_V2,
    {
      fetchPolicy: "network-only",
    }
  );

  // Dedicated fetch for the lead's VEHICLE OF INTEREST (by VIN), independent of
  // the main list, so the VOI always appears pinned at the top of the initial
  // view even if it isn't on page 1 of inventory. The main list stays
  // UNSEEDED (all inventory) so the panel is never empty; a typed search
  // replaces everything with plain search results.
  const [getVoiInventory, { data: voiData, loading: voiLoading, called: voiCalled }] = useLazyQuery(
    GET_LOCATION_VEHICLE_INVENTORY,
    {
      fetchPolicy: "cache-and-network",
    }
  );
  const [getVoiInventoriesV2, { data: voiDataV2, loading: voiLoadingV2, called: voiCalledV2 }] =
    useLazyQuery(GET_LOCATION_VEHICLE_INVENTORIES_V2, {
      fetchPolicy: "network-only",
    });

  useEffect(() => {
    if (!vehicleOfInterest?.vin || selectedLocations?.length === 0) return;
    const locationIds = selectedLocations
      .filter(item => item.isChecked && +item.id !== 0)
      .map(item => item.id);
    if (isMurdockLocation) {
      getVoiInventoriesV2({
        variables: { locationIds, search: vehicleOfInterest.vin, page: 1, pageSize: 5 },
      });
    } else {
      getVoiInventory({
        variables: {
          currentLocationId: currentLocationId,
          locationIds,
          isAllLocations: false,
          search: vehicleOfInterest.vin,
          page: 1,
          pageSize: 5,
        },
      });
    }
  }, [vehicleOfInterest, selectedLocations]);

  useEffect(() => {
    if (selectedLocations?.length > 0) {
      // Use V2 API only for location 448
      if (isMurdockLocation) {
        getVehicleInventoriesV2({
          variables: {
            locationIds: selectedLocations
              .filter(item => item.isChecked && +item.id !== 0)
              .map(item => item.id),
            search,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        });
      } else {
        // Use old API for other locations
        getVehicleInventory({
          variables: {
            currentLocationId: currentLocationId,
            locationIds: selectedLocations
              .filter(item => item.isChecked && +item.id !== 0)
              .map(item => item.id),
            isAllLocations: false,
            search,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        });
      }
    }
  }, [selectedLocations, search, pagination]);

  // OttoVin insertion: append the composed "Year Make Model Stock# link" block at the
  // BOTTOM of the composer. In the SMS textarea, a blank line separates it from
  // any existing message; in the email editor it goes in via handleInsertLink
  // (which hyperlinks the trailing URL and keeps the label as text).
  const insertOttoVinBlock = block => {
    if (textMode === "Email") {
      handleInsertLink(block);
      return;
    }
    const ctl = text.current;
    const current = ctl?.value ?? "";
    // Strip trailing whitespace so the separator is exactly one blank line.
    const base = current.replace(/\s+$/, "");
    const newValue = base.length ? `${base}\n\n${block}` : block;
    if (ctl) {
      ctl.value = newValue;
      ctl.focus();
      ctl.setSelectionRange(newValue.length, newValue.length);
      ctl.style.height = "inherit";
      ctl.style.height = `${ctl.scrollHeight}px`;
    }
    setmessageText(newValue);
  };

  const handleAddKeyword = keyword => {
    if (textMode === "Email") {
      handleInsertLink(keyword);
    } else {
      const ctl = document.getElementById("text");
      const currentValue = text.current.value;
      // Fall back to the end of the text when the textarea isn't focused, so the
      // link is appended instead of inserted at position 0.
      const startPos = ctl.selectionStart != null ? ctl.selectionStart : currentValue.length;

      const before = currentValue.slice(0, startPos);
      const after = currentValue.slice(startPos);

      // Ensure the inserted link is separated by a space from the surrounding
      // text so multiple shorten URLs don't get concatenated together.
      const needsLeadingSpace = before.length > 0 && !/\s$/.test(before);
      const needsTrailingSpace = after.length === 0 || !/^\s/.test(after);
      const insertText = `${needsLeadingSpace ? " " : ""}${keyword}${
        needsTrailingSpace ? " " : ""
      }`;

      text.current.value = [before, insertText, after].join("");
      if (text.current) {
        // Place the cursor right after the inserted link (and its trailing space).
        const newCursorPos = before.length + insertText.length;
        text.current.focus();
        text.current.setSelectionRange(newCursorPos, newCursorPos);
        text.current.style.height = "inherit";
        text.current.style.height = `${text.current.scrollHeight}px`;
      }
      setmessageText(text.current.value);
    }
  };

  const [createVdpLinks, { loading: vdpLinkLoader }] = useMutation(CREATE_VDP_LINK_INVENTORY);

  const parseLink = async () => {
    // One request per vehicle: the mutation resolves vdpLinks with an
    // unordered `id IN (...)` query, so a single batched call gives no safe
    // way to pair a link back to the vehicle whose name prefixes it. The UI
    // caps the selection at 3 and these run in parallel.
    const results = await Promise.all(
      selectedVehicle.map(async id => {
        try {
          const res = await createVdpLinks({ variables: { vehicleIds: [+id] } });
          const payload = res?.data?.createVdpLinksVehicleInventory;
          return {
            id,
            url: payload?.statusCode === 200 ? (payload?.vdpLinks || [])[0] : "",
            message: payload?.message,
          };
        } catch (error) {
          return { id, url: "", message: error?.message };
        }
      })
    );

    const succeeded = results.filter(result => !isEmpty(result.url));
    const failed = results.filter(result => isEmpty(result.url));

    if (failed.length > 0) {
      message.error(failed[0].message || lang.somethingWentWrong);
    }
    if (succeeded.length === 0) {
      return "";
    }
    message.success(succeeded[0].message);

    return succeeded
      .map(({ id, url }) => {
        const vehicleName = vehicleNamesRef.current.get(String(id));
        return vehicleName ? `${vehicleName} - check details here: ${url}` : url;
      })
      .join("\n");
  };

  // Notify parent about selected vehicles changes
  useEffect(() => {
    onSelectedVehiclesChange && onSelectedVehiclesChange(selectedVehicle);
  }, [selectedVehicle, onSelectedVehiclesChange]);

  useImperativeHandle(ref, () => ({
    getSelectedVehicles: () => selectedVehicle,
    addVehicles: async () => {
      if (ottoVinInsert && resolveLinks) {
        // OttoVin: resolver returns a { VIN: shortUrl } map. Compose one
        // "Year Make Model Stock#: link" line per selected vehicle (selection order) —
        // a colon separates the vehicle label from the link — then append the
        // block at the bottom of the composer.
        const linkMap = await resolveLinks(selectedVehicle);
        if (linkMap) {
          const block = selectedVehicle
            .map(id => selectedInfoRef.current.get(id))
            .filter(Boolean)
            .map(d => {
              const url = linkMap[String(d.vin || "").toUpperCase()];
              if (!url) return null; // link failed for this VIN — skip it
              const label = [
                d.year && +d.year !== 0 ? d.year : null,
                d.make || null,
                d.model || null,
                d.stockNumber || null,
              ]
                .filter(part => part != null && String(part).trim() !== "")
                .join(" ");
              return label ? `${label}: ${url}` : url;
            })
            .filter(Boolean)
            .join("\n");
          if (block) {
            insertOttoVinBlock(block);
          }
        }
      } else {
        const linkText = resolveLinks ? await resolveLinks(selectedVehicle) : await parseLink();
        // Guard: on failure the resolvers return undefined/null — inserting that
        // would put the literal string "undefined" into the message.
        if (linkText) {
          handleAddKeyword(linkText);
        }
      }
      onClose && onClose();
    },
  }));

  const getVinLink = (item, vdpUrl, isNew) => {
    let inventoryLink = "";
    if (vdpUrl && !isEmpty(vdpUrl)) {
      inventoryLink = vdpUrl;
    } else if (
      item?.location?.inventoryUrlFormat &&
      item?.location?.inventoryUrlFormat !== "" &&
      item?.location?.inventoryUrlFormat !== null
    ) {
      const url = item?.location?.inventoryUrlFormat || "";
      const isNewOrUsed = isNew ? "new" : "used";
      inventoryLink = url
        .replace("{vinNumber}", item?.vin)
        .replace("{vehicleUsedOrNew}", isNewOrUsed)
        .replace("{vehicleMake}", item?.make);
    }
    return inventoryLink;
  };

  const formatLocations = locations => {
    const checkedLocations = locations.filter(item => item.isChecked);
    if (checkedLocations.length <= 2) {
      return checkedLocations.map(item => item.title).join(", ");
    }
    const displayedLocations = checkedLocations
      .slice(0, 2)
      .map(item => item.title)
      .join(", ");
    return `${displayedLocations} and +${checkedLocations.length - 2} more`;
  };

  // Determine which data to use based on location
  const count = isMurdockLocation
    ? vdpDataV2?.getVehicleInventoriesV2?.count || 0
    : vdpData?.getVehicleInventoryV2?.count || 0;

  const rawVehicleList = isMurdockLocation
    ? vdpDataV2?.getVehicleInventoriesV2?.vehicleInventories || []
    : vdpData?.getVehicleInventoryV2?.data || [];

  // With a vehicle of interest and no typed search: the explicitly-fetched VOI
  // row(s) go first, then same-model (similar) vehicles, then the rest of
  // inventory. A typed search shows plain search results.
  const voiVin = (vehicleOfInterest?.vin || "").toUpperCase();
  const voiModel = (vehicleOfInterest?.model || "").toLowerCase();
  const voiFetched = (
    isMurdockLocation
      ? voiDataV2?.getVehicleInventoriesV2?.vehicleInventories || []
      : voiData?.getVehicleInventoryV2?.data || []
  ).filter(item => voiVin && (item?.vin || "").toUpperCase() === voiVin);
  const vehicleRank = item => {
    if (voiVin && (item?.vin || "").toUpperCase() === voiVin) return 0;
    if (voiModel && (item?.model || "").toLowerCase() === voiModel) return 1;
    return 2;
  };
  const vehicleList =
    vehicleOfInterest && !search
      ? [
          ...voiFetched,
          ...rawVehicleList.filter(item => (item?.vin || "").toUpperCase() !== voiVin),
        ].sort((a, b) => vehicleRank(a) - vehicleRank(b))
      : rawVehicleList;

  // Warn when the lead HAS a vehicle of interest but it isn't in inventory:
  // either it has no VIN to look up, or the VIN lookup settled with no match.
  // Suppressed while a typed search is active (the VOI view is replaced then).
  const voiLookupSettled = isMurdockLocation
    ? voiCalledV2 && !voiLoadingV2
    : voiCalled && !voiLoading;
  const voiNotFound =
    Boolean(vehicleOfInterest) &&
    !search &&
    (!voiVin || (voiLookupSettled && voiFetched.length === 0));
  const voiLabel =
    [vehicleOfInterest?.year, vehicleOfInterest?.make, vehicleOfInterest?.model]
      .filter(Boolean)
      .join(" ") ||
    vehicleOfInterest?.vin ||
    "";

  const isLoading = isMurdockLocation ? vdpLoaderV2 : vdpLoader;

  // The lead's vehicles of interest, reduced to the keys we can match against
  // an inventory row. Mirrors the source precedence used elsewhere in the
  // activity center (see MessageFeedback): inventory-linked VOIs first, then
  // the lead's free-text VOIs.
  const vehicleOfInterestKeys = useMemo(() => {
    const keys = new Set();
    [
      ...(selectedLead?.leadVehicleInventory || []),
      ...(selectedLead?.vehicleOfInterest || []),
    ].forEach(vehicle => {
      const vin = normalizeVehicleKey(vehicle?.vin);
      const stockNumber = normalizeVehicleKey(vehicle?.stockNumber);
      if (vin) keys.add(`vin:${vin}`);
      if (stockNumber) keys.add(`stock:${stockNumber}`);
    });
    return keys;
  }, [selectedLead?.leadVehicleInventory, selectedLead?.vehicleOfInterest]);

  // Normalize the two API shapes once, flag the lead's vehicle(s) of interest,
  // and hoist them to the top. Every other vehicle keeps the order the server
  // returned it in.
  const vehicles = useMemo(() => {
    const normalized = vehicleList.map(item => {
      const stockNumber = isMurdockLocation ? item?.stock_number : item?.stockNumber;
      const vin = normalizeVehicleKey(item?.vin);
      const stockKey = normalizeVehicleKey(stockNumber);
      return {
        item,
        vehicleId: isMurdockLocation ? item?.vehicle_id : item?.id,
        photos: isMurdockLocation
          ? (item?.list_of_photos || []).map(photo => photo.PhotoUrl)
          : (item?.vehicleInventoryMedias || []).map(media => media.mediaUrl),
        photoSrc: isMurdockLocation
          ? (item?.list_of_photos || [])[0]?.PhotoUrl
          : (item?.vehicleInventoryMedias || [])[0]?.mediaUrl,
        sellingPrice: isMurdockLocation ? item?.selling_price : item?.sellingPrice,
        currencyUnit: isMurdockLocation ? item?.currency_unit : item?.currencyUnit,
        locationId: isMurdockLocation ? item?.location_id : item?.locationId,
        stockNumber,
        modelNumber: isMurdockLocation ? item?.model_number : item?.modelNumber,
        exteriorColor: isMurdockLocation ? item?.exterior_color : item?.exteriorColor,
        interiorColor: isMurdockLocation ? item?.interior_color : item?.interiorColor,
        vdpUrl: isMurdockLocation ? item?.vdp_url : item?.vdpUrl,
        isNew: isMurdockLocation ? item?.is_new : item?.isNew,
        // Same title the card renders, so the inserted message always names
        // the vehicle exactly as the user saw it.
        name: [item?.year, item?.make, item?.model, item?.trim].filter(Boolean).join(" "),
        isVehicleOfInterest:
          (!!vin && vehicleOfInterestKeys.has(`vin:${vin}`)) ||
          (!!stockKey && vehicleOfInterestKeys.has(`stock:${stockKey}`)),
      };
    });

    return [
      ...normalized.filter(vehicle => vehicle.isVehicleOfInterest),
      ...normalized.filter(vehicle => !vehicle.isVehicleOfInterest),
    ];
  }, [vehicleList, isMurdockLocation, vehicleOfInterestKeys]);

  useEffect(() => {
    vehicles.forEach(({ vehicleId, name }) => {
      if (name) vehicleNamesRef.current.set(String(vehicleId), name);
    });
  }, [vehicles]);

  const paginationProps = {
    current: pagination.page,
    pageSize: pagination.pageSize,
    total: count || 0,
    showTotal: total => `${lang.total}: ${total} ${lang.vehicles}`,
    pageSizeOptions: ["25", "50", "100", "150", "200", "500"],
    showSizeChanger: true,
    onChange: (page, pageSize) => {
      setPagination({ page, pageSize });
    },
    onShowSizeChange: (current, size) => {
      setPagination({ page: current, pageSize: size });
    },
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center space-x-2 p-2">
        <VdpLocationSearch onChange={setSelectedLocations} onLoadingChange={setLocationLoading} />

        <DebounceInput
          className="w-full"
          placeholder="Search by vehicle name"
          suffix={
            <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} fill="none">
              <path
                fill="#64748B"
                stroke="#94A3B8"
                strokeWidth={0.078}
                d="m13 12.122-.022.027.025.026 3.912 3.91a.585.585 0 1 1-.83.83l-3.91-3.912-.026-.025-.027.023a6.844 6.844 0 1 1 .879-.879ZM2.087 7.75a5.67 5.67 0 0 0 5.664 5.664A5.664 5.664 0 1 0 2.086 7.75Z"
              />
            </svg>
          }
          value={search}
          onChange={value => {
            setsearch(value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
        />
      </div>
      {voiNotFound && (
        <div className="px-2 pb-2">
          <Alert
            type="warning"
            showIcon
            message={`Vehicle of interest${
              voiLabel ? ` (${voiLabel})` : ""
            } was not found in inventory.`}
          />
        </div>
      )}
      <div className="px-2 pb-2">
        <span className="text-black/[0.65] text-sm font-normal">
          Showing results for:{" "}
          <span className="text-black/[0.88]">
            {selectedLocations?.length > 1 &&
            selectedLocations?.every(item => item.isChecked === true)
              ? "All Locations"
              : formatLocations(selectedLocations)}
          </span>
        </span>
      </div>
      <List
        loading={isLoading || locationLoading}
        className="mt-2 flex-1 overflow-y-auto vdp-scrollbar-hide"
        dataSource={vehicles}
        renderItem={(vehicle, index) => {
          const {
            item,
            vehicleId,
            photos,
            photoSrc,
            sellingPrice,
            currencyUnit,
            locationId,
            stockNumber,
            modelNumber,
            exteriorColor,
            interiorColor,
            vdpUrl,
            isNew,
            name,
            isVehicleOfInterest,
          } = vehicle;

          return (
            <div
              key={vehicleId ?? index}
              onClick={() => {
                // Descriptor for the OttoVin "Year Make Model Stock# link" line, kept
                // in sync with the selection so it survives list/search changes.
                const descriptor = {
                  vin: item?.vin,
                  year: item?.year,
                  make: item?.make,
                  model: item?.model,
                  stockNumber,
                };
                setselectedVehicle(prevSelected => {
                  const alreadySelected = prevSelected.includes(vehicleId);
                  // Enforce the 3-vehicle cap: ignore new selections beyond it.
                  if (prevSelected.length >= 3 && !alreadySelected) {
                    return prevSelected;
                  }
                  if (alreadySelected) {
                    selectedInfoRef.current.delete(vehicleId);
                    return prevSelected.filter(id => id !== vehicleId);
                  }
                  selectedInfoRef.current.set(vehicleId, descriptor);
                  return [...prevSelected, vehicleId];
                });
              }}
              className={`flex flex-col p-4 gap-3 mb-4 cursor-pointer border rounded-xl border-solid ${
                selectedVehicle.includes(vehicleId)
                  ? "border-[#4096FF] bg-[#E6F4FF]"
                  : "border-[#D9D9D9]"
              }`}
            >
              {isVehicleOfInterest && (
                <Tag color="blue" className="w-fit">
                  {lang.vehicleOfInterest}
                </Tag>
              )}

              {/* Container 1 */}
              <div className="flex flex-row gap-3">
                <Image.PreviewGroup items={photos}>
                  <Image
                    onClick={e => e.stopPropagation()}
                    placeholder={
                      <Skeleton.Node style={{ width: 100, height: 70, borderRadius: 15 }} active />
                    }
                    fallback="https://funnel-webapp-uploads.s3.amazonaws.com/prod/images/generic-car-not-available-image.png"
                    className="h-[70px] object-cover w-[100px] rounded-[15px]"
                    src={photoSrc || ""}
                    alt="Vehicle"
                  />
                </Image.PreviewGroup>
                <div className="flex flex-col gap-1 w-full">
                  <div className="w-full flex justify-between items-start">
                    {voiVin && (item?.vin || "").toUpperCase() === voiVin && (
                      <Tag color="blue" className="mr-2 flex-shrink-0">
                        Vehicle of interest
                      </Tag>
                    )}
                    <span className="text-base text-black/[0.88] font-bold flex-1">
                      {getHighlightedText(name, search, "truncate block")}
                    </span>
                    <span
                      className="text-[#1677FF] text-sm cursor-pointer hover:underline ml-2 flex-shrink-0"
                      onClick={e => {
                        e.stopPropagation();
                        // Normalize data for VehicleInventoryDetailsDrawer
                        const normalizedVehicle = isMurdockLocation
                          ? {
                              id: item.vehicle_id,
                              locationId: item.location_id,
                              make: item.make,
                              model: item.model,
                              year: item.year,
                              trim: item.trim,
                              stockNumber: item.stock_number,
                              modelNumber: item.model_number,
                              vin: item.vin,
                              msrp: item.msrp,
                              sellingPrice: item.selling_price,
                              currencyUnit: item.currency_unit,
                              isNew: item.is_new,
                              exteriorColor: item.exterior_color,
                              interiorColor: item.interior_color,
                              vdpUrl: item.vdp_url,
                              vehicleInventoryMedias: (item.list_of_photos || []).map(photo => ({
                                mediaUrl: photo.PhotoUrl,
                              })),
                            }
                          : item;
                        setSelectedVehicleForInventoryDetails(normalizedVehicle);
                        setInventoryDetailsDrawerOpen(true);
                      }}
                    >
                      View
                    </span>
                  </div>
                  <span className="text-sm font-normal text-black/[0.88]">
                    <span>MSRP</span>{" "}
                    {item?.msrp || sellingPrice
                      ? `${currencies?.find(currency => currency.code === currencyUnit)?.symbol} ${
                          formatPrice(item?.msrp || sellingPrice) || "N/A"
                        }`
                      : "Price not available"}
                    <Tooltip title="Open inventory">
                      <LinkOutlined
                        onClick={e => {
                          e.stopPropagation();
                          window.open(getVinLink(item, vdpUrl, isNew));
                        }}
                        className="text-blue-500 ml-2 mt-1"
                      />
                    </Tooltip>
                  </span>
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width={10} height={14} fill="none">
                      <path
                        fill="#334155"
                        d="M5 3.5a2.187 2.187 0 1 0 0 4.375A2.187 2.187 0 0 0 5 3.5ZM5 7a1.313 1.313 0 1 1 0-2.625A1.313 1.313 0 0 1 5 7ZM5 .875A4.818 4.818 0 0 0 .187 5.688c0 1.717.794 3.537 2.297 5.263.676.78 1.436 1.483 2.267 2.095a.438.438 0 0 0 .502 0 13.9 13.9 0 0 0 2.263-2.095c1.5-1.726 2.296-3.546 2.296-5.264A4.818 4.818 0 0 0 5 .875Zm0 11.266c-.904-.711-3.938-3.323-3.938-6.454a3.937 3.937 0 1 1 7.875 0c0 3.13-3.033 5.743-3.937 6.454Z"
                      />
                    </svg>
                    <span className="ml-2 text-xs font-normal text-black/[0.65]">
                      {selectedLocations?.map(data => {
                        if (+data?.id === +locationId) {
                          return data?.title;
                        }
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Container 2 */}
              <div
                className={`${
                  selectedVehicle.includes(vehicleId) ? "bg-white" : "bg-[#F5F5F5]"
                } rounded-xl p-4 flex flex-col font-[400] gap-1`}
              >
                <DetailRow label="VIN" value={getHighlightedText(item?.vin || "VIN N/A", search)} />
                <DetailRow
                  label="Stock No"
                  value={getHighlightedText(stockNumber || "N/A", search)}
                />
                <DetailRow
                  label="Model No"
                  value={getHighlightedText(modelNumber || "N/A", search)}
                />
                <DetailRow
                  label="Color"
                  value={
                    <>
                      {getHighlightedText(exteriorColor || "N/A", search)} (E)
                      {", "}
                      {getHighlightedText(interiorColor || "N/A", search)} (I)
                    </>
                  }
                />
              </div>

              {isMurdockLocation && (
                <Button
                  type="link"
                  className="!p-0 !w-fit !text-[#1677FF]"
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedVehicleForDetails(item);
                    setMoreDetailsDrawerOpen(true);
                  }}
                >
                  View more details
                </Button>
              )}
            </div>
          );
        }}
        pagination={null}
        itemLayout="horizontal"
      />
      <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200 pt-2 mt-auto">
        <Pagination size="small" {...paginationProps} />
      </div>

      {moreDetailsDrawerOpen && (
        <Drawer
          title="More information"
          placement="right"
          width={600}
          onClose={() => {
            setMoreDetailsDrawerOpen(false);
            setSelectedVehicleForDetails(null);
          }}
          open={moreDetailsDrawerOpen}
        >
          <VehicleMoreDetails vehicle={selectedVehicleForDetails} />
        </Drawer>
      )}

      <VehicleInventoryDetailsDrawer
        open={inventoryDetailsDrawerOpen}
        onClose={() => {
          setInventoryDetailsDrawerOpen(false);
          setSelectedVehicleForInventoryDetails(null);
        }}
        vehicleData={selectedVehicleForInventoryDetails}
      />

      <style jsx>{`
        /* For Webkit-based browsers (Chrome, Safari and Opera) */
        .vdp-scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        /* For IE, Edge and Firefox */
        .vdp-scrollbar-hide {
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */
        }
      `}</style>
    </div>
  );
});

VDPSelector.displayName = "VDPSelector";

export default VDPSelector;
